import Alert from '../../../../ui/alert/Alert';
import Input from '../../../../ui/input/Input';
import useLabels from '../../../../hooks/useLabels';
import useInfo from '../../../../hooks/useInfo';
import ChatWelcome from '../../../../elements/chat-welcome/ChatWelcome';
import useStateRef from '../../../../hooks/useStateRef';
import { getClassNames } from '../../../../helpers/classHelpers';
import { FC, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../../../../ui/icon/Icon';
import { Icons } from '../../../../ui/icon/icons/material';
import { IconTheme } from '../../../../ui/icon/Icon.types';
import { TestIds } from '../../../../mocks/ids';
import { useNavigate } from 'react-router-dom';
import { AppRoute } from '../../../../router/Routing';
import { ChatViewLayoutProps } from '../../../../layouts/chat-view-layout/ChatViewLayout';
import { TourCatalogTargets } from '../../../tours/hooks/useTourCatalog';
import { useGetMediaSizes } from '../../../../hooks/useMediaSize';
import { SvgIcons } from '../../../../ui/icon/icons/svg';
import { useLayout } from '../../../../contexts/layout/LayoutContext';
import styles from './PromptGallery.module.scss';
import { usePrompt } from '../../contexts/PromptContext';
import { useAppInfo } from '../../../../contexts/app-info/AppInfoContext';
import { Prompt } from '../../models/PromptTypes';
import { CustomSelectOptionBaseType } from '@danfoss/mosaic-core';
import { useChat } from '../../../../contexts/chat/ChatContext';
import PromptViewModal from '../../modals/prompt-view-modal/PromptViewModal';
import WindowHeader from '../../../../elements/window-header/WindowHeader';
import { categoryOptions } from '../../config';
import { useAdaptive } from '../../../../contexts/adaptive/AdaptiveContext';
import { container, FilterConfig, filterPromptKeysByLastUsed, filterPrompts, handleSelectChange, personasListExtras, renderCard, sevenDaysAgo } from '../../helpers/PromptHelper';
import PromptFilters from './prompt-filters/PromptFilters';
import { filterBy, searchBy, sortItemsBy, sortItemsWith } from '../../../../helpers/listsHelpers';
import Loader from '../../../../ui/loader/Loader';

const MAX_VISIBLE_PROMPTS = 5;
const MIN_POPULAR_USAGE_COUNT = 5;
const MAX_VISIBLE_FAVORITE_PROMPTS = 5;

const PromptGallery: FC<ChatViewLayoutProps> = ({ onOpenSidebar }) => {
    const l = useLabels();
    const navigate = useNavigate();
    const { prompts, isLoading } = usePrompt();
    const { userId } = useAppInfo();

    const [search, setSearch] = useState<string>('');
    const [showAllPrompts, setShowAllPrompts] = useState(true);
    const [showAllFavPrompts, setShowAllFavPrompts] = useState(true);
    const [showAllPopularPrompts, setshowAllPopularPrompts] = useState(true);
    const [showAllLastSeven, setShowAllLastSeven] = useState(true);
    const { persistent: { mergeInput } } = useChat();
    const { catalogFavorites } = useAdaptive();

    const offsetTopRef = useRef<number>();
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const [headerTitleVisible, setHeaderTitleVisible, headlineTitleVisibleRef] = useStateRef<boolean>();
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const [selectedAssistantPrompts, setSelectedAssistantPrompts] = useState<string[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedPrompt, setSelectedPrompt] = useState<Prompt | null>(null);

    const { allowedPersonaOptions } = useInfo();

    const labels = useMemo(() => ({
        headline: l.promptGalleryHeadline,
        description: l.promptGalleryDescription,
        searchPlaceholder: l.promptGallerySearchPlaceholder,
        noResultsFoundOnSearch: l.promptGalleryNoResults,
        noResultsFound: l.promptGalleryNoResultsFound,
        catPopular: l.promptGalleryPopular,
        catFavorites: l.promptGalleryFavorites,
        lastSevenDays: l.lastSevenDays,
        lastThirtyDays: l.lastThirtyDays,
        filterAssistant: l.filterAssistant,
        filterCategory: l.filterTask,
        filterCreatedByMe: l.filterCreatedByMe,
        promptManagementTitle: l.promptManagementMenuTitle,
        all: l.all,
        seeAll: l.seeMore,
        seeLess: l.seeLess,
        promptGalleryError: l.promptGalleryError,
    }), [l]);

    useLayoutEffect(() => {
        if (!searchContainerRef.current) return;
        offsetTopRef.current = searchContainerRef.current?.offsetTop;
    }, []);

    const handleScroll = () => {
        if (!searchContainerRef.current) return;
        const offsetTop = searchContainerRef.current.offsetTop;

        if (offsetTop === offsetTopRef.current && !headlineTitleVisibleRef.current) {
            setHeaderTitleVisible(false);
            headlineTitleVisibleRef.current = true;
        }
        else if (offsetTop !== offsetTopRef.current && headlineTitleVisibleRef.current) {
            setHeaderTitleVisible(true);
            headlineTitleVisibleRef.current = false;
        }
    };

    const onClickCard = useCallback((persona: Prompt) => {
        const prompt = prompts.find(p => p.key === persona.key);
        if (prompt) {
            setSelectedPrompt(prompt);
        }
    }, [prompts]);

    const handleTryPrompt = useCallback((prompt: Prompt) => {
        mergeInput(prompt.assistant_key, { message: prompt.content, promptTemplateId: prompt.key });
        const persona = allowedPersonaOptions.find(p => p.key === prompt.assistant_key);
        const route = persona?.route ?? prompt.assistant_key;
        navigate(route ? "/" + route : AppRoute.chat);
    }, [mergeInput, navigate, allowedPersonaOptions]);

    const renderCardFn = useMemo(
        () =>
            renderCard({
                onClickCard,
                catalogFavorites,
            }),
        [onClickCard, catalogFavorites]
    );

    const favoritePromptIds = useMemo(
        () => catalogFavorites.prompts_pinned ?? [],
        [catalogFavorites.prompts_pinned]
    );

    const personas = useMemo(() => {
        const filterConfig: FilterConfig = {
            createdBy: isChecked ? userId : undefined,
            assistantKeys: selectedAssistantPrompts,
            categories: selectedCategories,
        };
        return filterPrompts(prompts, filterConfig);
    }, [prompts, userId, isChecked, selectedAssistantPrompts, selectedCategories]);

    const searchedPrompts = useMemo(
        () => searchBy(personas, search, ['title', 'tags']),
        [personas, search]
    );

    const favoritePrompts = useMemo(() => {
        if (favoritePromptIds.length === 0) return [];
        return filterBy(searchedPrompts, [{ key: 'key', values: favoritePromptIds }], []);
    }, [favoritePromptIds, searchedPrompts]);

    const nonFavoritePrompts = useMemo(() =>
        filterBy(searchedPrompts, [{ key: 'key', values: favoritePromptIds }], [{ key: 'key', predicate: (value, item) => !favoritePromptIds.includes(item.key) }]
        ), [searchedPrompts, favoritePromptIds]);

    const favoritePromptKeys = useMemo(
        () => favoritePrompts.map(p => p.key),
        [favoritePrompts]
    );

    const topPopularPromptKeys = useMemo(() => {
        const popular = nonFavoritePrompts
            .filter(p => {
                const fullPrompt = prompts.find(fp => fp.key === p.key);
                return fullPrompt?.last_used !== null && p.usage_count >= MIN_POPULAR_USAGE_COUNT;
            });
        return sortItemsBy(popular, 'usage_count', true)
            .map(p => p.key)
            .filter(key => !favoritePromptKeys.includes(key));
    }, [nonFavoritePrompts, prompts, favoritePromptKeys]);

    const favoriteList = useMemo(() =>
        sortItemsWith(
            favoritePrompts,
            item => item.title,
            false
        ).map(renderCardFn(showAllFavPrompts)(MAX_VISIBLE_FAVORITE_PROMPTS, false, 'favorite')),
        [favoritePrompts, renderCardFn, showAllFavPrompts]
    );

    const topPopularList = useMemo(() => {
        const popularItems = topPopularPromptKeys
            .map(key => nonFavoritePrompts.find(p => p.key === key)!)
            .filter(item => item.usage_count >= MIN_POPULAR_USAGE_COUNT);

        const sortedPopular = sortItemsWith(popularItems, item => item.usage_count, true);

        return sortedPopular.map(renderCardFn(showAllPopularPrompts)(MAX_VISIBLE_PROMPTS, false, 'topPopular'));
    }, [topPopularPromptKeys, nonFavoritePrompts, renderCardFn, showAllPopularPrompts]);

    const usedPromptKeysSet = useMemo(() => new Set([
        ...favoritePromptKeys,
        ...topPopularPromptKeys,
    ]), [favoritePromptKeys, topPopularPromptKeys]);

    const nonFavoritePromptKeys = useMemo(
        () => nonFavoritePrompts.map(p => p.key),
        [nonFavoritePrompts]
    );

    const usedLast7DaysKeys = useMemo(
        () => filterPromptKeysByLastUsed(
            prompts,
            nonFavoritePromptKeys,
            sevenDaysAgo,
            usedPromptKeysSet
        ),
        [prompts, nonFavoritePromptKeys, usedPromptKeysSet]
    );

    const otherPromptKeys = useMemo(() =>
        nonFavoritePromptKeys.filter(
            key =>
                !usedPromptKeysSet.has(key) &&
                !usedLast7DaysKeys.includes(key)
        ),
        [nonFavoritePromptKeys, usedPromptKeysSet, usedLast7DaysKeys]
    );

    const lastSevenDays = useMemo(() => {
        const recentItems = usedLast7DaysKeys
            .map(key => nonFavoritePrompts.find(p => p.key === key)!)
            .filter(Boolean);

        return sortItemsWith(
            recentItems,
            item => item.title,
            false
        ).map(renderCardFn(showAllLastSeven)(MAX_VISIBLE_PROMPTS, false));
    }, [usedLast7DaysKeys, renderCardFn, showAllLastSeven, nonFavoritePrompts]);

    const allPrompts = useMemo(() => {
        const recentItems = otherPromptKeys
            .map(key => nonFavoritePrompts.find(p => p.key === key)!)
            .filter(Boolean);

        return sortItemsWith(
            recentItems,
            item => item.title,
            false
        ).map(renderCardFn(true)(MAX_VISIBLE_PROMPTS, true));
    }, [otherPromptKeys, nonFavoritePrompts, renderCardFn]);


    const lastSevenToShow = showAllLastSeven ? lastSevenDays : lastSevenDays.slice(0, MAX_VISIBLE_PROMPTS);
    const allFavPromptsToShow = showAllFavPrompts ? favoriteList : favoriteList.slice(0, MAX_VISIBLE_FAVORITE_PROMPTS);
    const topPopularToShow = showAllPopularPrompts ? topPopularList : topPopularList.slice(0, MAX_VISIBLE_FAVORITE_PROMPTS);
    const allPromptsToShow = showAllPrompts ? allPrompts : allPrompts.slice(0, MAX_VISIBLE_PROMPTS);

    const handleAssistantChange = useCallback(
        (option?: string | string[] | CustomSelectOptionBaseType | CustomSelectOptionBaseType[]) => {
            handleSelectChange(option, setSelectedAssistantPrompts);
        }, []
    );

    const handleCategoryChange = useCallback(
        (option?: string | string[] | CustomSelectOptionBaseType | CustomSelectOptionBaseType[]) => {
            handleSelectChange(option, setSelectedCategories);
        }, []
    );

    const handleResetFilter = useCallback(() => {
        setSearch('');
        setIsChecked(false);
        setSelectedAssistantPrompts([]);
        setSelectedCategories([]);
    }, []);

    const { collapseLeftSidebar, expandLeftSidebar, isLeftSidebarVisible, isRightSidebarVisible } = useLayout();
    const { isLarge, isMedium } = useGetMediaSizes();

    const showIcon = useMemo(() => {
        if (!isLarge && onOpenSidebar) return false;
        if (isLeftSidebarVisible && (isLarge || isMedium)) return true;
        if (!isRightSidebarVisible && (isLarge || isMedium)) return true;
        if (isRightSidebarVisible && !isLeftSidebarVisible) return true;
        return false;
    }, [isLarge, onOpenSidebar, isLeftSidebarVisible, isMedium, isRightSidebarVisible]);

    const left = <>
        {showIcon && <Icon.Svg
            theme={IconTheme.dark}
            title={isLeftSidebarVisible ? '[Collapse sidebar]' : '[Expand sidebar]'}
            iconName={isLeftSidebarVisible ? SvgIcons.LeftPanelClose : SvgIcons.LeftPanelOpen}
            onClick={isLeftSidebarVisible ? () => collapseLeftSidebar() : expandLeftSidebar}
        />}
        {!showIcon && !!onOpenSidebar && <Icon.Base title={l.menuIconTitle} iconName={Icons.menu} onClick={onOpenSidebar} />}
    </>;

    const renderSearch = <div className={styles.search}>
        <Input id={TourCatalogTargets.search} placeholder={labels.searchPlaceholder} value={search} onChange={setSearch} />
    </div>;

    const hasAnyPrompts = favoriteList.length > 0 || topPopularList.length > 0 || lastSevenDays.length > 0 || allPrompts.length > 0;

    return (
        <>
            <PromptViewModal
                isOpen={!!selectedPrompt}
                onClose={() => setSelectedPrompt(null)}
                prompt={selectedPrompt}
                assistantTitle={
                    selectedPrompt
                        ? (allowedPersonaOptions.find(p => p.key === selectedPrompt.assistant_key)?.title ?? '')
                        : ''
                }
                onTry={handleTryPrompt}
            />
            <WindowHeader
                className={getClassNames([styles['header-title'], !headerTitleVisible && styles.visible])}
                left={left}
                center={renderSearch}
            />
            <div className={styles.catalog} data-testid={TestIds.catalogView} onScroll={handleScroll}>
                <div className={styles.welcome}>
                    <ChatWelcome avatar={Icons.promptGallery}
                        persona={{ title: labels.headline, description: labels.description, key: '', route: '', type: 'chat', versions: [], }} />
                </div>
                <div className={styles.content}>
                    <Loader isVisible={isLoading} />
                    {!isLoading && (
                        <>
                            <PromptFilters
                                labels={labels}
                                allowedPersonaOptions={allowedPersonaOptions.map(({ key, title }) => ({ value: key, label: title }))}
                                selectedAssistantPrompts={selectedAssistantPrompts}
                                selectedCategories={selectedCategories}
                                categoryOptions={categoryOptions}
                                isChecked={isChecked}
                                setIsChecked={setIsChecked}
                                handleResetFilter={handleResetFilter}
                                handleAssistantChange={handleAssistantChange}
                                handleCategoryChange={handleCategoryChange}
                                onPromptManagementNavigate={() => navigate(AppRoute.promptManagement)}
                            />
                            {!hasAnyPrompts ? (
                                <Alert className={styles.notification} description={search ? labels.noResultsFoundOnSearch : labels.noResultsFound} />
                            ) : (
                                <div className={styles.body}>
                                    {!!favoriteList.length &&
                                        container(
                                            labels.catFavorites,
                                            allFavPromptsToShow,
                                            personasListExtras(favoriteList.length, showAllFavPrompts, setShowAllFavPrompts, MAX_VISIBLE_FAVORITE_PROMPTS, labels.seeAll, labels.seeLess),
                                            TourCatalogTargets.personaList
                                        )
                                    }

                                    {!!topPopularList.length &&
                                        container(
                                            labels.catPopular,
                                            topPopularToShow,
                                            personasListExtras(topPopularList.length, showAllPopularPrompts, setshowAllPopularPrompts, MAX_VISIBLE_FAVORITE_PROMPTS, labels.seeAll, labels.seeLess),
                                            TourCatalogTargets.personaList
                                        )
                                    }

                                    {!!lastSevenDays.length &&
                                        container(
                                            labels.lastSevenDays, lastSevenToShow, personasListExtras(lastSevenDays.length, showAllLastSeven, setShowAllLastSeven, MAX_VISIBLE_PROMPTS, labels.seeAll, labels.seeLess
                                            ),
                                            !favoriteList.length ? TourCatalogTargets.personaList : undefined
                                        )}

                                    {!!allPrompts.length && container(labels.all, allPromptsToShow, personasListExtras(allPrompts.length, showAllPrompts, setShowAllPrompts, MAX_VISIBLE_PROMPTS, labels.seeAll, labels.seeLess),
                                        !favoriteList.length ? TourCatalogTargets.personaList : undefined)}

                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default PromptGallery;
